import { ChevronDown, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../atoms/button';
import { TableCell, TableRow } from '../table';
import { CollapsibleBandRowProps, ExpandCollapseAllProps } from './interface';

/**
 * A table row that heads a group (a week) and folds it away. The whole band is
 * one button, so it works with a keyboard and tells screen readers whether the
 * group is open. It keeps the same text color on hover and only tints the
 * background a little, so the label stays readable in light and dark.
 */
function CollapsibleBandRow({ colSpan, expanded, onToggle, children }: CollapsibleBandRowProps) {
  return (
    <TableRow className="bg-border hover:bg-border">
      <TableCell colSpan={colSpan} className="p-0">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={onToggle}
          className="flex w-full items-center gap-2 px-4 py-2 text-left text-secondary-foreground transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring dark:hover:bg-white/10"
        >
          <ChevronDown aria-hidden="true" className={cn('h-4 w-4 shrink-0 transition-transform', !expanded && '-rotate-90')} />
          <span className="flex flex-wrap items-baseline">{children}</span>
        </button>
      </TableCell>
    </TableRow>
  );
}

/** One button for the whole list: collapses every group, or opens them all again. */
function ExpandCollapseAll({ allExpanded, onExpandAll, onCollapseAll, disabled }: ExpandCollapseAllProps) {
  const Icon = allExpanded ? ChevronsDownUp : ChevronsUpDown;
  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-2"
      disabled={disabled}
      onClick={allExpanded ? onCollapseAll : onExpandAll}
    >
      <Icon className="h-4 w-4" />
      {allExpanded ? 'Collapse all' : 'Expand all'}
    </Button>
  );
}

export { CollapsibleBandRow, ExpandCollapseAll };
export type { CollapsibleBandRowProps, ExpandCollapseAllProps };
